import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  Collapse,
  CircularProgress,
  Popover,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../../app/context/useApp";
import CreateActionIcon from "../../../ui/components/CreateActionIcon";
import GlassSearchBar from "../../../ui/components/GlassSearchBar";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
  homeGlassShadow,
} from "../constants/homeGlass";
import useScrollFade from "../../../ui/hooks/useScrollFade";
import {
  consumeConversationsShouldExpand,
  dispatchMinimizeProjects,
  dispatchConversationsOpened,
  EXPAND_CONVERSATIONS_EVENT,
  MINIMIZE_CONVERSATIONS_EVENT,
} from "../../../layout/sidebarAccordionEvents";

export default function ChatHistoryList() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    chatSessions,
    loadChatSession,
    deleteChatSession,
    renameChatSession,
    renamingConversationIds,
    selectedClient,
    createNewChat,
    currentChatSession,
    isGenerating,
    showAlert,
    conversations,
    privateConversations,
    selectedSilo,
    loadingConversations,
    loadingPrivateConversations,
    deletingConversationIds,
    chatType,
    selectedSiloIsDefault,
    setActiveMode,
  } = useApp();

  const [, setRenderTrigger] = useState(0);
  const [isConversationSectionOpen, setIsConversationSectionOpen] = useState(
    () => consumeConversationsShouldExpand(),
  );
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const conversationListScroll = useScrollFade({
    disabled: !isConversationSectionOpen || !isHistoryOpen,
  });
  const [conversationSearch, setConversationSearch] = useState("");
  const [isConversationSearchOpen, setIsConversationSearchOpen] = useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [renameAnchorEl, setRenameAnchorEl] = useState(null);
  const [pendingRenameSession, setPendingRenameSession] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [chatMenuSession, setChatMenuSession] = useState(null);
  const getSessionId = (session) =>
    session?.conversationsId ||
    session?.conversationId ||
    session?.ConversationsId ||
    session?.ConversationId ||
    session?.conversations_id ||
    session?.conversation_id ||
    session?.id;

  useEffect(() => {
    dispatchConversationsOpened(isConversationSectionOpen);
    return () => dispatchConversationsOpened(false);
  }, [isConversationSectionOpen]);

  useEffect(() => {
    const handleMinimizeConversations = () => {
      setIsHistoryOpen(false);
      setIsConversationSearchOpen(false);
      setConversationSearch("");
    };
    const handleExpandConversations = () => {
      setIsConversationSectionOpen(true);
      setIsHistoryOpen(true);
    };

    window.addEventListener(
      MINIMIZE_CONVERSATIONS_EVENT,
      handleMinimizeConversations,
    );
    window.addEventListener(EXPAND_CONVERSATIONS_EVENT, handleExpandConversations);
    return () => {
      window.removeEventListener(
        MINIMIZE_CONVERSATIONS_EVENT,
        handleMinimizeConversations,
      );
      window.removeEventListener(
        EXPAND_CONVERSATIONS_EVENT,
        handleExpandConversations,
      );
    };
  }, []);

  const hadActiveChatRef = useRef(Boolean(currentChatSession));
  useEffect(() => {
    const hasActiveChat = Boolean(currentChatSession);
    if (hasActiveChat && !hadActiveChatRef.current) {
      setIsConversationSectionOpen(true);
      dispatchMinimizeProjects();
    }
    hadActiveChatRef.current = hasActiveChat;
  }, [currentChatSession]);

  const getConversationKey = (session) => {
    const conversationId = getSessionId(session);
    if (conversationId) return `conv:${String(conversationId)}`;

    // Fallback only when API row has no conversation id at all.
    const title = session?.title || session?.silosName || 'New Chat';
    const created = session?.createdAt || session?.date || "";
    return `fallback:${String(title)}:${String(created)}`;
  };

  const selectedClientKey =
    selectedClient?.clientId ?? selectedClient?.Id ?? selectedClient?.id ?? "";
  const getRowClientId = (session) =>
    session?.clientId ??
    session?.client_Id ??
    session?.ClientId ??
    session?.client_id ??
    "";
  const matchesSelectedClient = (session) => {
    const rowClientId = getRowClientId(session);
    if (!rowClientId || !selectedClientKey) return true;
    return String(rowClientId) === String(selectedClientKey);
  };

  // Default-selected (home) silo can return multiple rows per conversation.
  // Keep only one (latest) item per conversation ID.
  const clientSessions = selectedSiloIsDefault
    ? (() => {
        const sortedDefaultSessions = [...(conversations || [])].sort(
          (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
        );
        const uniqueByConversationId = new Set();
        return sortedDefaultSessions.filter((session) => {
          const conversationKey = getConversationKey(session);
          if (uniqueByConversationId.has(conversationKey)) return false;
          uniqueByConversationId.add(conversationKey);
          return true;
        });
      })()
    : (() => {
        const displaySessions =
          chatType === "private" ? privateConversations : conversations;
        if (chatType === "private") {
          return displaySessions
            .filter(matchesSelectedClient)
            .sort(
              (a, b) =>
                new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
            );
        }
        return displaySessions
          .filter(
            (s) => matchesSelectedClient(s) && s.chatType !== "private",
          )
          .sort(
            (a, b) =>
              new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
          );
      })();

  const conversationSearchQuery = conversationSearch.trim().toLowerCase();
  const isBriefRoute = location.pathname.includes("/brief");
  const conversationListHeading =
    isBriefRoute
      ? chatType === "private"
        ? "Private chats"
        : "Team chats"
      : "Your chats";
  const conversationSearchLabel = isBriefRoute
    ? conversationListHeading.toLowerCase()
    : "chats";
  const displaySessions =
    !conversationSearchQuery
      ? clientSessions
      : clientSessions.filter((session) => {
          const label = (
            session?.title ||
            session?.silosName ||
            "New Chat"
          ).toLowerCase();
          return label.includes(conversationSearchQuery);
        });

  const sessionMatchesCurrentChat = (session) => {
    if (!session || !currentChatSession) return false;
    const rowId = getSessionId(session);
    const curId = getSessionId(currentChatSession);
    if (rowId != null && curId != null && String(rowId) === String(curId)) return true;
    const la = session?.id;
    const lb = currentChatSession?.id;
    return la != null && lb != null && String(la) === String(lb);
  };

// const handleconversationbyid = (id) => {
//   const conversation = conversations.find((c) => c.id === id);
  
//   if (conversation) {
//     loadChatSession(conversation.id);
//   }
// };
  useEffect(() => {
    setRenderTrigger((prev) => prev + 1);
  }, [selectedClient, chatSessions, conversations, privateConversations, selectedSilo, chatType]);

  const handleLoad = async (session) => {
    if (isGenerating) {
      showAlert("Response in progress", "Please wait...");
      return;
    }

    setIsConversationSectionOpen(true);
    setIsHistoryOpen(true);
    dispatchMinimizeProjects();
    await loadChatSession(session);
    if (!isBriefRoute) {
      setActiveMode(null);
      if (location.pathname !== "/") navigate("/");
    }
  };

  const closeDeletePopover = () => {
    setDeleteAnchorEl(null);
    setPendingDelete(null);
  };

  const closeRenamePopover = () => {
    setRenameAnchorEl(null);
    setPendingRenameSession(null);
    setRenameDraft("");
  };

  const closeChatMenu = () => {
    setChatMenuAnchor(null);
    setChatMenuSession(null);
  };

  const beginRenameSession = (session, anchorEl) => {
    if (isGenerating) {
      showAlert("Response in progress", "Please wait...");
      return;
    }
    const conversationId = getSessionId(session);
    if (!conversationId || String(conversationId).startsWith("chat-")) {
      showAlert("Rename unavailable", "This chat is not saved yet.");
      return;
    }
    setRenameAnchorEl(anchorEl);
    setPendingRenameSession(session);
    setRenameDraft(
      String(
        session?.title ||
          session?.silosName ||
          session?.conversationTitle ||
          "",
      ).trim() || "New Chat",
    );
  };

  const handleOpenChatMenu = (event, session) => {
    event.stopPropagation();
    if (isGenerating) {
      showAlert("Response in progress", "Please wait...");
      return;
    }
    const rowEl = event.currentTarget?.closest?.("[data-chat-row]");
    setChatMenuAnchor(rowEl || event.currentTarget);
    setChatMenuSession(session);
  };

  const handleMenuRename = (event) => {
    event.stopPropagation();
    const session = chatMenuSession;
    const anchor = chatMenuAnchor;
    closeChatMenu();
    if (session) beginRenameSession(session, anchor);
  };

  const handleMenuDelete = (event) => {
    event.stopPropagation();
    const session = chatMenuSession;
    const anchor = chatMenuAnchor;
    closeChatMenu();
    if (!session) return;
    if (isGenerating) {
      showAlert(
        "Response in progress",
        "Please wait until the response finishes before deleting chats.",
      );
      return;
    }
    setDeleteAnchorEl(anchor);
    setPendingDelete({
      id: getSessionId(session),
      title: session?.title || session?.silosName || "New Chat",
    });
  };

  const confirmRename = async () => {
    if (!pendingRenameSession) return;
    const ok = await renameChatSession(pendingRenameSession, renameDraft);
    if (ok) closeRenamePopover();
  };

  const confirmDelete = () => {
    if (pendingDelete?.id) {
      deleteChatSession(pendingDelete.id, { skipConfirm: true });
    }
    closeDeletePopover();
  };

  const toggleConversationSection = () => {
    setIsConversationSectionOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        setIsHistoryOpen(true);
        dispatchMinimizeProjects();
      } else {
        setIsConversationSearchOpen(false);
        setConversationSearch("");
      }
      return nextOpen;
    });
  };

  const toggleConversationHistory = () => {
    setIsHistoryOpen((prev) => {
      const nextOpen = !prev;
      if (!nextOpen) {
        setIsConversationSearchOpen(false);
        setConversationSearch("");
      }
      return nextOpen;
    });
  };

  const handleConversationSearchOpenChange = (nextOpen) => {
    setIsConversationSearchOpen(nextOpen);
    if (!nextOpen) setConversationSearch("");
  };

  return (
    <Box
      sx={{
        pb: 2,
        flex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={isConversationSectionOpen}
        onClick={toggleConversationSection}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleConversationSection();
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          mb: 0.75,
          flexShrink: 0,
          color: "rgba(234,231,226,0.45)",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { color: "rgba(234,231,226,0.85)" },
        }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: 16,
            color: "inherit",
            transform: isConversationSectionOpen ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 160ms ease",
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "inherit",
            lineHeight: 1.2,
          }}
        >
          Conversation
        </Typography>
      </Box>

      {isConversationSectionOpen && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
      <Box
        role="button"
        tabIndex={0}
        aria-label="New chat"
        onClick={() => {
          if (selectedClient || selectedSiloIsDefault) {
            setIsConversationSectionOpen(true);
            dispatchMinimizeProjects();
            createNewChat(selectedClient);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (selectedClient || selectedSiloIsDefault) {
              setIsConversationSectionOpen(true);
              dispatchMinimizeProjects();
              createNewChat(selectedClient);
            }
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.45,
          mb: 1,
          py: 0.35,
          px: 0.25,
          flexShrink: 0,
          cursor: "pointer",
          userSelect: "none",
          color: "rgba(234,231,226,0.88)",
          "&:hover": { color: "#fff" },
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
          New chat
        </Typography>
        <CreateActionIcon sx={{ fontSize: 22, flexShrink: 0, opacity: 0.85 }} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexShrink: 0 }}>
        <Tooltip title={isHistoryOpen ? "Close chats" : "Open chats"} placement="left-start">
          <IconButton
            size="small"
            onClick={toggleConversationHistory}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.055))",
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 16px rgba(0,0,0,0.14)",
              backdropFilter: "blur(18px) saturate(116%)",
              WebkitBackdropFilter: "blur(18px) saturate(116%)",
              color: "rgba(234,231,226,0.9)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))",
              },
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 12,
                display: "grid",
                gridTemplateColumns: "4px 1fr",
                gap: "2px",
                transform: "rotate(90deg)",
              }}
            >
              <Box
                sx={{
                  borderRadius: "2px",
                  backgroundColor: "currentColor",
                  opacity: isHistoryOpen ? 1 : 0.85,
                }}
              />
              <Box
                sx={{
                  borderRadius: "2px",
                  border: "1px solid currentColor",
                  opacity: 0.7,
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse
        in={isHistoryOpen}
        timeout={260}
        collapsedSize={0}
        mountOnEnter
        unmountOnExit
        easing={{
          enter: "cubic-bezier(0.22, 1, 0.36, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        }}
        sx={{
          flexShrink: 0,
          overflow: "hidden",
          transformOrigin: "top",
          "& .MuiCollapse-wrapperInner": {
            transformOrigin: "top",
            opacity: isHistoryOpen ? 1 : 0,
            transform: isHistoryOpen ? "translateY(0)" : "translateY(-6px)",
            transition:
              "opacity 180ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.9,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              maxWidth: isConversationSearchOpen ? 0 : 120,
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: isConversationSearchOpen ? 0 : 1,
              transition:
                "max-width 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                color: "rgba(234,231,226,0.58)",
                fontWeight: 700,
              }}
            >
              {conversationListHeading}
            </Typography>
          </Box>
          <GlassSearchBar
            value={conversationSearch}
            onChange={setConversationSearch}
            open={isConversationSearchOpen}
            onOpenChange={handleConversationSearchOpenChange}
            height={28}
            collapsedSize={24}
            placeholder={`Search ${conversationSearchLabel}...`}
            ariaLabel={`Search ${conversationSearchLabel}`}
            openTooltip={`Search ${conversationSearchLabel}`}
            inputSx={{
              fontSize: 11,
              "&::placeholder": {
                color: "rgba(234,231,226,0.54)",
              },
            }}
            sx={{
              ml: "auto",
              flex: isConversationSearchOpen ? "1 1 100%" : "0 0 24px",
              width: isConversationSearchOpen ? "100%" : 24,
              borderRadius: isConversationSearchOpen ? "999px" : "6px",
            }}
          />
        </Box>
      </Collapse>

      <Collapse
        in={isHistoryOpen}
        timeout={320}
        collapsedSize={0}
        mountOnEnter
        unmountOnExit
        easing={{
          enter: "cubic-bezier(0.22, 1, 0.36, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transformOrigin: "top",
          "& .MuiCollapse-wrapper": {
            flex: 1,
            minHeight: 0,
            height: "100% !important",
            display: "flex",
            flexDirection: "column",
            transformOrigin: "top",
          },
          "& .MuiCollapse-wrapperInner": {
            flex: 1,
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            transformOrigin: "top",
            opacity: isHistoryOpen ? 1 : 0,
            transform: isHistoryOpen ? "translateY(0)" : "translateY(-8px)",
            transition:
              "opacity 220ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          },
        }}
      >
        <Box
          ref={conversationListScroll.scrollRef}
          onScroll={conversationListScroll.onScroll}
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarGutter: "stable",
            pr: 0.35,
            ...conversationListScroll.scrollFadeSx,
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.28)",
              borderRadius: "4px",
            },
          }}
        >
        {loadingConversations ||
        (!selectedSiloIsDefault && loadingPrivateConversations) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography sx={{ color: "rgba(234,231,226,0.5)", fontSize: 12 }}>
            Loading conversations...
          </Typography>
          </Box>
        ) : clientSessions.length === 0 ? (
          <Typography
          sx={{
            color: "rgba(234,231,226,0.5)",
            textAlign: "left",
            fontSize: 12,
          }}
          >
          {selectedSiloIsDefault
            ? "No conversations in this silo yet"
            : chatType === "private"
              ? "No private chats yet"
              : selectedSilo
                ? "No conversations in this silo"
                : "No chats yet"}
          </Typography>
        ) : displaySessions.length === 0 ? (
          <Typography
            sx={{
              color: "rgba(234,231,226,0.5)",
              textAlign: "left",
              fontSize: 12,
            }}
          >
            No matching conversations
          </Typography>
        ) : (
          displaySessions.map((session) => {
            const isActive = sessionMatchesCurrentChat(session);
            const conversationTitle = session?.title || session?.silosName || "New Chat";
            return (
            <Box
            key={getConversationKey(session)}
            data-chat-row
            onClick={() => handleLoad(session)}
            sx={{
              minHeight: 31,
              py: 0.45,
              px: 0.75,
              mb: 0.45,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: "8px",
              border: isActive
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid transparent",
              color: isActive
                ? "rgba(242, 238, 232, 0.98)"
                : "rgba(234,231,226,0.76)",
              background: isActive
                ? "linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))"
                : "rgba(255,255,255,0.025)",
              transition:
                "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
              "&:hover": {
                background: isActive
                  ? "linear-gradient(90deg, rgba(255,255,255,0.14), rgba(255,255,255,0.055))"
                  : "rgba(255,255,255,0.07)",
              },
              "&:hover .chat-row-actions-btn": {
                opacity: 1,
              },
            }}
            >
            <ChatBubbleOutlineIcon
              sx={{
                fontSize: 13,
                color: isActive ? "rgba(255,255,255,0.88)" : "rgba(234,231,226,0.54)",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 10.5,
                lineHeight: 1.25,
                fontWeight: isActive ? 600 : 500,
                flex: 1,
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "inherit",
              }}
              title={conversationTitle}
            >
              {conversationTitle}
            </Typography>
            {(() => {
              const conversationId = getSessionId(session);
              if (!conversationId) return null;
              const isDeleting = !!deletingConversationIds?.[String(conversationId)];
              const isRenaming = !!renamingConversationIds?.[String(conversationId)];
              const iconColor = isActive
                ? "rgba(255,255,255,0.55)"
                : "rgba(255,255,255,0.4)";
              const iconOpacity = (busy) =>
                busy ? 1 : isActive ? 0.9 : 0;
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    gap: 0.25,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    className="chat-row-actions-btn"
                    size="small"
                    disabled={isDeleting || isRenaming || isGenerating}
                    onClick={(e) => handleOpenChatMenu(e, session)}
                    sx={{
                      color: iconColor,
                      p: 0.25,
                      width: 18,
                      height: 18,
                      opacity: iconOpacity(isDeleting || isRenaming),
                      transition: "opacity 160ms ease, color 160ms ease",
                      "&:hover": { color: "#fff" },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
                    }}
                    title="Conversation actions"
                  >
                    {isDeleting || isRenaming ? (
                      <CircularProgress size={11} thickness={5.5} color="inherit" />
                    ) : (
                      <MoreVertIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Box>
              );
            })()}
            </Box>
          );
          })
        )}
        </Box>
      </Collapse>

      {isHistoryOpen && (
        <Divider
          sx={{
            mt: 1.3,
            flexShrink: 0,
            borderColor: "rgba(255,255,255,0.1)",
          }}
        />
      )}
        </Box>
      )}

      <Popover
        open={Boolean(deleteAnchorEl && pendingDelete)}
        anchorEl={deleteAnchorEl}
        onClose={closeDeletePopover}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 0.8,
            px: 1.2,
            py: 1.2,
            borderRadius: "10px",
            background: homeGlassHero.rest,
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            border: HOME_GLASS_BORDER,
            boxShadow: homeGlassShadow.rest,
            minWidth: 220,
          },
        }}
      >
        <Typography sx={{ fontSize: 11, color: "rgba(234,231,226,0.75)", mb: 0.8 }}>
          Delete this chat?
        </Typography>
        <Typography
          sx={{
            fontSize: 11.5,
            color: "#fff",
            mb: 1,
            maxWidth: 210,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={pendingDelete?.title || ""}
        >
          {pendingDelete?.title || "Conversation"}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.8 }}>
          <Button
            onClick={closeDeletePopover}
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              color: "rgba(234,231,226,0.8)",
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, #a855f7, #d946ef)",
              "&:hover": {
                background: "linear-gradient(135deg, #9333ea, #c026d3)",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>

      <Menu
        anchorEl={chatMenuAnchor}
        open={Boolean(chatMenuAnchor)}
        onClose={closeChatMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 0.5,
            minWidth: 168,
            borderRadius: "10px",
            background: homeGlassHero.rest,
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            border: HOME_GLASS_BORDER,
            boxShadow: homeGlassShadow.rest,
            "& .MuiMenuItem-root": {
              fontSize: 12,
              py: 0.85,
              color: "rgba(234,231,226,0.92)",
            },
          },
        }}
      >
        <MenuItem onClick={handleMenuRename} dense>
          <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
            <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} dense sx={{ color: "rgba(255,200,200,0.95)" }}>
          <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(renameAnchorEl && pendingRenameSession)}
        anchorEl={renameAnchorEl}
        onClose={closeRenamePopover}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 0.8,
            px: 1.2,
            py: 1.2,
            borderRadius: "10px",
            background: homeGlassHero.rest,
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            border: HOME_GLASS_BORDER,
            boxShadow: homeGlassShadow.rest,
            minWidth: 260,
          },
        }}
      >
        <Typography sx={{ fontSize: 11, color: "rgba(234,231,226,0.75)", mb: 0.8 }}>
          Rename chat
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              confirmRename();
            }
          }}
          sx={{
            mb: 1,
            "& .MuiOutlinedInput-root": {
              fontSize: 12,
              color: "#fff",
              "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.8 }}>
          <Button
            onClick={closeRenamePopover}
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              color: "rgba(234,231,226,0.8)",
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRename}
            disabled={
              !String(renameDraft ?? "").trim() ||
              !!renamingConversationIds?.[
                String(getSessionId(pendingRenameSession) ?? "")
              ]
            }
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, #a855f7, #d946ef)",
              "&:hover": {
                background: "linear-gradient(135deg, #9333ea, #c026d3)",
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}